<template>
  <div>
    <div class="tw:flex tw:items-center">
      <div>
        <FilterBar>
          <GenericFilter :types="issuingCompanies" v-model="selectedCarriers" title="Issuing Company" chip-title-field="label" />
          <GenericFilter :types="agencyTypes" v-model="selectedAgencyTypes" title="CAC Entity" chip-title-field="label" data-test="agencyTypes" data-test-confirm="agencyTypesConfirm"/>
          <CoverageTypeFilter v-model="selectedCoverageTypes" :coverageTypes="coverageTypes" @update:model-value="refreshData"/>
          <GenericFilter :types="departments" v-model="selectedDepartments" title="Department"/>
          <GenericFilter :types="profitCenters" v-model="selectedProfitCenters" title="Profit Center" chip-title-field="label"/>
          <div class="tw:flex tw:items-center">
            <a v-show="filterApplied" @click="clearAllFilters()" class="tw:link">Reset&nbsp;Filters</a>
          </div>
        </FilterBar>
      </div>
      <ExportMenu :disabled="!exportEnabled" :items="downloadMenuOptions" @export="handleExport" />
    </div>

    <div class="tw:h-1.5 tw:mt-2">
      <transition name="fade">
        <div v-if="showProgressBar" class="tw:flex tw:justify-center">
          <progress class="tw:progress tw:w-56 tw:progress-secondary tw:rounded-none tw:h-2" :value="loadingProgress" max="8"></progress>
        </div>
      </transition>
    </div>
    <PageProgress v-if="loading"/>
    <div v-if="!loading" class="inner">
      <div class="pb-8">
        <Statistics id="kpiPanel" dense no-borders :statistics="statistics"/>
        <main class="tw:mt-4">
          <div class="carrierCharts">
            <div data-test="premiumTotalByMonth" id="premiumTotalByMonthChart">
              <fusioncharts type="mscolumn2d" width="100%" height="400" dataFormat="json" :dataSource="premiumTotalByMonth"></fusioncharts>
            </div>
            <div data-test="premiumTotalVSOthers" id="premiumTotalVSOthersChart">
              <fusioncharts type="stackedcolumn2d" width="100%" height="400" dataFormat="json" :dataSource="premiumTotalVSOthers" />
            </div>
          </div>
          <div class="carrierCharts">
            <div data-test="premiumByCarrier" id="premiumByCoverageChart">
              <fusioncharts type="stackedbar2d" width="100%" height="400" dataFormat="json" :dataSource="premiumTotalByCoverageType"></fusioncharts>
            </div>
            <div data-test="premiumByAgency" id="premiumByAgencyChart">
              <fusioncharts type="stackedbar2d" width="100%" height="400" dataFormat="json" :dataSource="premiumTotalByAgency"></fusioncharts>
            </div>
          </div>
        </main>
        <div class="tw:flex tw:flex-col tw:justify-center">
          <div class="tw:flex tw:ml-14 tw:items-center tw:gap-8">
            <h4 class="tw:font-semibold tw:text-black ">Premium by Distribution (Trailing 12 Months)</h4>
            <div class="tw:join tw:justify-center">
              <input type="radio" name="wholesalerFilterOptions" aria-label="All" class="tw:btn tw:join-item tw:btn-sm"
                     @click="wholesalerFilter = WholesalerFilter.ALL" :checked="wholesalerFilter === WholesalerFilter.ALL" />

              <input type="radio" name="wholesalerFilterOptions" aria-label="Retail" class="tw:btn tw:join-item tw:btn-sm"
                     @click="wholesalerFilter = WholesalerFilter.RETAIL" :checked="wholesalerFilter === WholesalerFilter.RETAIL" />
              
              <input type="radio" name="wholesalerFilterOptions" aria-label="Wholesale" class="tw:btn tw:join-item tw:btn-sm"
                     @click="wholesalerFilter = WholesalerFilter.WHOLESALE" :checked="wholesalerFilter === WholesalerFilter.WHOLESALE" />
            </div>
          </div>

          <div data-test="premiumByWholesaler" id="premiumByWholesalerChart">
            <fusioncharts type="sankey" width="100%" height="700" dataFormat="json" :dataSource="premiumByWholesaler"></fusioncharts>
          </div>
        </div>
        <div class="dataTables">
          <CarrierShareOfWalletTable ref="carrierShareOfWalletTable" id="carrierShareOfWalletTable" @loaded="tablesLoaded = true" :insurerGroupId="insurerGroupId" :carrierIds="selectedCarriers" :agencyTypes="selectedAgencyTypes" :coverageTypes="selectedCoverageTypes" :departments="selectedDepartments"/>

          <!-- top clients table -->
          <div v-if="tablesLoaded" class="tw:flex tw:flex-col tw:m-4 tw:items-center">
            <template v-if="(carrierTopClientsData || []).length > 0">
              <div class="tw:max-w-(--breakpoint-2xl) tw:w-full">
                <div class="tw:flex tw:m-2 tw:justify-center">
                  <h2 class="tw:text-base tw:font-bold tw:text-[#0d0d0d] tw:font-roboto tw:text-[16px] tw:text-left">Top Clients</h2>
                </div>

                <div class="tw:overflow-auto tw:p-4 tw:pt-0">
                  <div class="tw:relative">
                    <table class="tw:mr-1 apptable dense --tight-first" id="topClientsTable">
                      <thead class="tw:text-[0.8rem]">
                      <tr>
                        <td class="nosort tw:p-4">Client</td>
                        <td class="nosort tw:p-4">{{ carrier.name }} Coverage Types</td>
                        <td class="nosort tw:p-4">Industries</td>
                        <td class="nosort tw:p-4">{{ carrier.name }} Premium</td>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="client in carrierTopClientsData" :key="client.accountID">
                        <td class="tw:m-2 tw:flex-col tw:gap-1 tw:align-middle">
                          <div class="tw:text-[0.8rem]">
                            <router-link
                                :to="`/account/${client.accountID}/overview`"
                                class="url-text tw:link-hover tw:text-[0.8rem] tw:text-black"
                            >{{ client.accountName }}</router-link>
                          </div>
                          <div v-if="client.domainName" class="tw:link tw:text-xs">
                            <a
                                v-if="client.domainName"
                                :href="'//' + `${client.domainName.trim()}`"
                                target="_blank"
                                rel="noopener noreferrer"
                            >{{ client.domainName }}</a
                            >
                          </div>
                        </td>
                        <td>
                          <div class="coverages tw:m-1 tw:flex tw:flex-wrap tw:gap-1" v-if="client.coverageTypes.length > 0">
                            <Badge v-for="coverage in client.coverageTypes" :key="coverage">
                              <div class="tw:text-[0.8rem]">{{ coverage }}</div>
                            </Badge>
                          </div>
                        </td>
                        <td class="tw:p-2 tw:text-[0.8rem]">{{ client.industries }}</td>
                        <td class="tw:p-2 tw:text-[0.8rem]">{{ filters.compactMoney(client.totalPremium) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <PageProgress v-else />
        </div>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
import PageProgress from '@/components/PageProgress.vue';
import { paletteColors, stackedSeriesColors, triSeriesColors } from '@/utilities/Color';
import Statistics from '@/components/Statistics.vue';
import { carrierService, referenceService } from '@/services/api';
import { compactNumber, percentage } from '@/utilities/Number';
import { compactMoney } from '@/utilities/Money';
import GenericFilter from '@/components/GenericFilter.vue';
import FilterBar from '@/components/FilterBar.vue';
import CoverageTypeFilter from '@/components/CoverageTypeFilter.vue';
import CarrierShareOfWalletTable from '@/views/carrier/CarrierShareOfWalletTable.vue';
import ExportMenu from '@/components/ExportMenu.vue';
import { exportToPDF, exportToPNG, getChartImages, getHeaderImage, getPNG } from '@/utilities/ExportCharts';
import {Carrier, CarrierPremiumByWholesaler, CarrierTopClients, WholesalerFilter} from '@/types/Carrier';
import { premiumByWholesalerToSankey } from '@/utilities/Carrier';
import { Statistic } from '@/types/Statistics';
import { MultiSeries } from '@/types/MultiSeries';
import { FilterType, LabelFilterType } from '@/types/components/FilterType';
import { defineComponent } from 'vue';
import Store from '@/store';
import { filters } from '@/utilities/Filters';
import Badge from '@/components/Badge.vue';

const premiumTotalByMonthChart = {
  animation: '1',
  caption: 'Premium Total by Month',
  xAxisName: 'Month',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'Premium Total by Month',
  showLegend: true,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const premiumTotalVSOthersChart = {
  animation: '1',
  caption: 'Premium Total vs Others (Trailing 12 Months)',
  xAxisName: 'Carrier',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'Premium Total vs Others',
  showLegend: true,
  rotateLabels: true,
  slantLabels: true,
  showSum: true,
  showValues: false,
  paletteColors: stackedSeriesColors,
  forceYAxisValueDecimals: false,
  decimals: 1,
};

const premiumTotalByAgencyChart = {
  animation: '1',
  caption: 'Premium by CAC Entity (YTD Comparison)',
  yAxisName: 'Premium',
  forceYAxisValueDecimals: false,
  theme: 'cacs',
  exportFilename: 'CAC + CAH Premium',
  showLegend: true,
  legendPosition: 'right-top',
  decimals: 1,
  rotateLabels: true,
  slantLabels: true,
  showValues: false,
  showSum: true,
  paletteColors: stackedSeriesColors,
};

const premiumTotalByCoverageTypeChart = {
  animation: '1',
  caption: 'Premium Total by Coverage Type (Trailing 12 Months)',
  xAxisName: 'Coverage Type',
  yAxisName: 'Premium',
  theme: 'cacs',
  exportFilename: 'Premium Total by Coverage Type',
  showLegend: true,
  legendPosition: 'right-top',
  forceYAxisValueDecimals: false,
  decimals: 1,
  showValues: false,
  showSum: true,
  paletteColors: stackedSeriesColors,
};

const premiumByWholesalerChart = {
  animation: '1',
  // caption: 'Premium by Distribution (Trailing 12 Months)', // caption is set in the component
  yAxisName: 'Premium (Millions)',
  theme: 'cacs',
  exportFilename: 'Premium by Distribution',
  showLegend: false,
  numberPrefix: '$',
  linkColor: 'blend',
  nodeLabelFontSize: 13,
  decimals: '1',
  valueFontBold: true,
  labelFontBold: true,
  exportFormats: 'PNG=Export PNG File|SVG=Export SVG for PowerPoint',
  chartTopMargin: 0,
};

interface Data {
  loading: boolean;
  carrier: Carrier;
  statistics: Statistic[];
  premiumTotalByMonthData?: MultiSeries;
  premiumTotalVSOthersData?: MultiSeries;
  premiumTotalByCoverageTypeData?: MultiSeries;
  premiumTotalVSCAHData?: MultiSeries;
  premiumByWholesalerResult?: CarrierPremiumByWholesaler[];
  carrierTopClientsData?: CarrierTopClients[];
  issuingCompanies: LabelFilterType<number>[];
  selectedCarriers: number[];
  agencyTypes: LabelFilterType<string>[];
  selectedAgencyTypes: string[];
  profitCenters: LabelFilterType<number>[];
  selectedProfitCenters: number[];
  coverageTypes: FilterType<string>[];
  selectedCoverageTypes: string[];
  departments: LabelFilterType<string>[];
  selectedDepartments: string[];
  agencyColorMap: Map<string, string>;
  downloadList: string[];
  wholesalerFilter: WholesalerFilter;
  tablesLoaded: boolean;
  loadingProgress: number;
  showProgressBar: boolean;
}

export default defineComponent({
  name: 'CarrierOverview',
  components: { Badge, PageProgress, Statistics, GenericFilter, FilterBar, CoverageTypeFilter, CarrierShareOfWalletTable, ExportMenu },
  data(): Data {
    return {
      loading: true,
      carrier: {} as Carrier,
      statistics: [],
      premiumTotalByMonthData: undefined,
      premiumTotalVSOthersData: undefined,
      premiumTotalByCoverageTypeData: undefined,
      premiumTotalVSCAHData: undefined,
      premiumByWholesalerResult: undefined,
      carrierTopClientsData: undefined,
      issuingCompanies: [],
      selectedCarriers: [],
      agencyTypes: [],
      selectedAgencyTypes: [],
      profitCenters: [],
      selectedProfitCenters: [],
      coverageTypes: [],
      selectedCoverageTypes: [],
      departments: [],
      selectedDepartments: [],
      agencyColorMap: new Map(),
      downloadList: [],
      wholesalerFilter: WholesalerFilter.ALL,
      tablesLoaded: false,
      loadingProgress: 0,
      showProgressBar: false,
    };
},
  computed: {
    filters() {
      return filters
    },
    WholesalerFilter() {
      return WholesalerFilter
    },
    premiumTotalByMonth() {
      return { chart: { ...premiumTotalByMonthChart, paletteColors: [...triSeriesColors].reverse() }, ...this.premiumTotalByMonthData };
    },
    premiumTotalVSOthers() {
      return { chart: premiumTotalVSOthersChart, ...this.premiumTotalVSOthersData };
    },
    premiumTotalByCoverageType() {
      return { chart: premiumTotalByCoverageTypeChart, ...this.premiumTotalByCoverageTypeData };
    },
    premiumTotalByAgency() {
      return {
        chart: {
          ...{ ...premiumTotalByAgencyChart, caption: `${this.carrier.name} - Premium by CAC Entity (YTD Comparison)` },
          paletteColors
        }, ...this.premiumTotalVSCAHData
      };
    },
    premiumByWholesaler() {
      return { chart: premiumByWholesalerChart, ...this.premiumByWholesalerData };
    },
    insurerGroupId() {
      const id = parseInt(this.$route.params.id as string);
      return isNaN(id) ? -1 : id;
    },
    downloadMenuOptions() {
      return [
        'KPI Panel',
        'Premium Total By Month',
        'Premium Total vs Others (TTM)',
        'Premium Total by Coverage (TTM)',
        'Premium by CAC Entity (YTD)',
        'Premium by Distribution (TTM)',
        'Carrier Share of Wallet (TTM)',
        'Carrier Share of Wallet (YTD)',
      ];
    },
    premiumByWholesalerData() {
      return premiumByWholesalerToSankey(this.premiumByWholesalerResult!, this.carrier.name, this.wholesalerFilter);
    },
    filterApplied() {
      return this.selectedAgencyTypes.length > 0
          || this.selectedDepartments.length > 0
          || this.selectedCarriers.length > 0
          || this.selectedCoverageTypes.length > 0
          || this.selectedProfitCenters.length > 0;
    },
    exportEnabled() {
      return !this.loading && this.tablesLoaded;
    },
  },
  methods: {
    async refreshData() {
      this.loading = true;
      this.loadingProgress = 0;
      this.showProgressBar = true;
      this.tablesLoaded = false;

      const carrierId = this.$route.params.id as string;
      this.carrier = await carrierService.findById(carrierId);
      
      const incrementProgress = () => {
        this.loadingProgress++;
      };

      const [
        premiumTotalByMonthResult,
        premiumTotalVSOthersResult,
        premiumTotalByCoverageTypeResult,
        premiumTotalVSCAHResult,
        premiumByWholesalerResult,
        kpi,
        carrierTopClientsResult,
      ] = await Promise.all([
        carrierService.premiumTotalByMonth(carrierId, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.premiumTotalVSOthers(carrierId, this.carrier.name, 15, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.premiumTotalByCoverageType(carrierId, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.premiumTotalByAgency(carrierId, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedAgencyTypes, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.premiumByWholesaler(carrierId, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.kpi(carrierId, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
        carrierService.carrierTopClients(carrierId, this.selectedAgencyTypes, this.selectedCoverageTypes, this.selectedDepartments, this.selectedCarriers, this.selectedProfitCenters).then(res => { incrementProgress(); return res; }),
      ]);
      this.premiumTotalByMonthData = premiumTotalByMonthResult;
      this.premiumTotalVSOthersData = premiumTotalVSOthersResult;
      this.premiumTotalByCoverageTypeData = premiumTotalByCoverageTypeResult;
      this.premiumTotalVSCAHData = premiumTotalVSCAHResult;
      this.premiumByWholesalerResult = premiumByWholesalerResult;
      this.carrierTopClientsData = carrierTopClientsResult.map((c) => ({
        ...c,
        coverageTypes: (c.coverageTypes && typeof c.coverageTypes === "string") ? c.coverageTypes.split(",") : c.coverageTypes,
      }));
      
      this.statistics = [
        { key: 'client-count', value: compactNumber(kpi.clientCount), heading: 'Client Count', loading: false, classes: ['dense'] },
        { key: 'policy-count', value: compactNumber(kpi.policyCount), heading: 'Policy Count', loading: false, classes: ['dense'] },
        { key: 'premium-ytd', value: compactMoney(kpi.premiumYtd), heading: 'Premium YTD', loading: false, classes: ['dense'] },
        { key: 'premium-previous-year', value: compactMoney(kpi.premiumPreviousYear), heading: 'Premium Previous Year', loading: false, classes: ['dense'] },
        { key: 'premium-ttm', value: compactMoney(kpi.premiumTtm), heading: 'Premium TTM', loading: false, classes: ['dense'] },
        { key: 'shareOfBookTTM', value: percentage(kpi.shareOfBookTtm), heading: 'Share of Book TTM', loading: false, classes: ['dense'] },
        { key: 'marketYield', value: percentage(kpi.marketYield), heading: 'Market Yield', loading: false, classes: ['dense'], toolTip: 'Market Yield is calculated from billed commission and premium. Fees, audit charges, and policies without commission are excluded.' },
        { key: 'rawYield', value: percentage(kpi.rawYield), heading: 'Raw Yield', loading: false, classes: ['dense'], toolTip: 'Raw Yield is calculated using total revenue (fees, audit charges, and billed commission) and premium.' },
      ];

      this.loading = false;
    },
    async loadReferenceData() {
      const carrierId = this.$route.params.id as string;

      const [
        agencyTypes,
        coverageTypes,
        departments,
        issuingCompanies,
        profitCenters,
      ] = await Promise.all([
        referenceService.agencyTypes(),
        referenceService.coverageTypes(),
        referenceService.departments(),
        carrierService.issuingCompanies(carrierId),
        referenceService.profitCenters(),
      ]);

      this.agencyTypes = agencyTypes.map<LabelFilterType<string>>(a => ({ label: a.agencyName, value: a.agencyCode })).sort((a, b) => a.label.localeCompare(b.label));
      this.coverageTypes = coverageTypes.map<FilterType<string>>(t => ({ text: t.label, value: t.label }));
      this.departments = departments;
      this.issuingCompanies = issuingCompanies.map<LabelFilterType<number>>(c => ({ label: c.issuingCompanyName, value: c.issuingCompanyId })).sort((a, b) => a.label.localeCompare(b.label));
      this.profitCenters = profitCenters.map<LabelFilterType<number>>(p => ({ label: p.name, value: p.id })).sort((a, b) => a.label.localeCompare(b.label));
    },
    async handleExport(type, charts, finishExport) {
      // gather element IDs for each chart
      const elementIds = charts.map(c => {
        if (c === 'KPI Panel') {
          return 'kpiPanel';
        } else if (c === 'Premium Total By Month') {
          return 'premiumTotalByMonthChart';
        } else if (c === 'Premium Total vs Others (TTM)') {
          return 'premiumTotalVSOthersChart';
        } else if (c === 'Premium Total by Coverage (TTM)') {
          return 'premiumByCoverageChart';
        } else if (c === 'Premium by CAC Entity (YTD)') {
          return 'premiumByAgencyChart';
        } else if (c === 'Premium by Distribution (TTM)') {
          return 'premiumByWholesalerChart';
        } else if (c === 'Carrier Share of Wallet (TTM)') {
          return 'carrierShareOfWalletTable';
        } else if (c === 'Carrier Share of Wallet (YTD)') {
          return 'carrierShareOfWalletTableYTD';
        } else {
          console.log(`unexpected chart ${c}`);
        }
      }).filter(id => id !== null);

      // create the PNG image that will be used for exporting
      const images = await getChartImages(elementIds);

      // if YTD carrier share of wallet table is selected, handle that here since the chart needs to be toggled before capture
      if (elementIds.some(id => id === 'carrierShareOfWalletTableYTD')) {
        (this.$refs.carrierShareOfWalletTable as InstanceType<typeof CarrierShareOfWalletTable>).changeTableDataType('YTD');
        await new Promise((resolve) => setTimeout(resolve, 500)); // wait for the table to update

        const image = await getChartImages(['carrierShareOfWalletTable']);
        images.push(...image);
      }

      const maxWidth = Math.max(...images.map(({ width }) => width));
      const header = await getHeaderImage(maxWidth, this.carrier.name);
      
      // create the PNG image that will be used for exporting
      const png = await getPNG(images, header);

      const currentDate = new Date();
      const formattedDate = currentDate.toISOString().split('T')[0].replace(/-/g, '.');
      const tenantName = Store.state.user?.tenantName;
      const filename = `${tenantName}-${this.carrier.name}_${formattedDate}`;
      
      if (type === 'png') {
        await exportToPNG(png, filename + '.png');
      } else if (type === 'pdf') {
        await exportToPDF(images, header, filename + '.pdf');
      } else {
        console.log(`unexpected value ${type} for export type`);
      }

      finishExport();
    },
    clearAllFilters() {
      this.selectedAgencyTypes = [];
      this.selectedDepartments = [];
      this.selectedCarriers = [];
      this.selectedCoverageTypes = [];
      this.selectedProfitCenters = [];
    },
  },
  async created() {
    await this.loadReferenceData();
    await this.refreshData();
  },
  watch: {
    selectedAgencyTypes() {
      this.refreshData();
    },
    selectedDepartments() {
      this.refreshData();
    },
    selectedCarriers() {
      this.refreshData();
    },
    selectedProfitCenters() {
      this.refreshData();
    },
    tablesLoaded() {
      if (this.tablesLoaded) {
        this.loadingProgress++;

        if (this.loadingProgress >= 8) {
          setTimeout(() => {
            this.showProgressBar = false;
          }, 500);
        }

        // Wait for DOM update
        this.$nextTick(() => {
          // set max height for the top clients table to height of the share of wallet table - overflow will scroll
          const t1 = document.getElementById("shareOfWalletTable");
          const t2 = document.getElementById("topClientsTable");
          
          if (t1 && t2) {
            const h1 = t1.offsetHeight || 0;
            
            // Get the parent container that needs the height constraint
            const t2Container = t2.parentElement?.classList.contains('tw:overflow-auto') ? t2.parentElement : t2.closest('[class*="tw:overflow-auto"]');
            
            if (t2Container) {
              (t2Container as HTMLElement).style.height = `${h1}px`;
              (t2Container as HTMLElement).style.overflowY = 'auto';
            }
          }
        });
      }
    }
  }
});
</script>

<style scoped>
.carrierCharts {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  gap: 50px;
}

@media (min-width: 768px) {
  .carrierCharts > div {
    min-width: 700px;
    max-width: 700px;
  }
}

@media (min-width: 1024px) {
  .carrierCharts > div {
    min-width: 900px;
    max-width: 900px;
  }
}

.dataTables {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  align-items: start;
  gap: 50px;
}

@media (min-width: 768px) {
  .dataTables > div {
    min-width: 700px;
    max-width: 700px;
  }
}

@media (min-width: 1024px) {
  .dataTables > div {
    min-width: 900px;
    max-width: 900px;
  }
}

.wholesalerChart > div {
  max-width: 2000px;
  min-width: 400px;
  flex-grow: 1;
}

.coverages {
  margin-top: 1em;
  max-width: 20vw;
}

.coverages > .v-chip {
  margin-left: 0.5em;
  margin-right: 0.5em;
  flex: 1 0 auto;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
